import { OccupancyRestrictionsInterface } from '@valk-nx/core/lib/interfaces/hotels.interface';

import { PriceCalculationInterface, PriceInterface } from './price.interface';

const minimumLoyalStep = 250;
const maximumLoyalStep = 500;

export class PriceHelper {
	static generateLoyalToSpend(maxAmount: number): number[] {
		if (maxAmount < minimumLoyalStep) {
			return [0];
		}

		const loyalList = [0, minimumLoyalStep];
		const maxIncrements = Math.floor(maxAmount / maximumLoyalStep);

		for (let i = 1; i <= maxIncrements; i++) {
			const currentAmount = maximumLoyalStep * i;
			loyalList.push(currentAmount);

			if (
				i === maxIncrements &&
				maxAmount % maximumLoyalStep >= minimumLoyalStep
			) {
				loyalList.push(currentAmount + minimumLoyalStep);
			}
		}

		return loyalList;
	}

	static determineMaxLoyalFromRooms(rooms: PriceInterface[]): number {
		return rooms
			.map((room) => room.pricing)
			.map((pricing) => {
				const { deposit, price } = pricing;
				const isFullPayment =
					deposit.total === price.totalIncTax || deposit.total === 0;
				return isFullPayment
					? price.total
					: price.total - deposit.total;
			})
			.reduce(
				(sum, amount) =>
					sum +
					Math.floor(amount / minimumLoyalStep) * minimumLoyalStep,
				0,
			);
	}

	static calculateMunicipalTax(rooms: PriceCalculationInterface[]): number {
		return (
			rooms?.reduce((acc, obj) => acc + obj.pricing.tax.municipal, 0) || 0
		);
	}

	static calculateTouristTax(rooms: PriceCalculationInterface[]): number {
		return (
			rooms?.reduce((acc, obj) => acc + obj.pricing.tax.tourist, 0) || 0
		);
	}

	static calculateTotalTax(rooms: PriceCalculationInterface[]): number {
		return rooms?.reduce((acc, obj) => acc + obj.pricing.tax.total, 0) || 0;
	}

	static calculateTotalPrice(
		rooms: PriceCalculationInterface[],
		loyal = 0,
	): number {
		const totalPrice =
			rooms?.reduce(
				(acc, obj) => acc + obj.pricing.price.totalIncTax,
				0,
			) || 0;

		return totalPrice - loyal;
	}

	static calculateTotalPriceWithoutTax(
		rooms: PriceCalculationInterface[],
	): number {
		return (
			rooms?.reduce((acc, obj) => acc + obj.pricing.price.total, 0) || 0
		);
	}

	static calculateTotalDeposit(
		rooms: PriceCalculationInterface[],
		loyal = 0,
	): number {
		const totalDeposit =
			rooms?.reduce((acc, obj) => acc + obj.pricing.deposit.total, 0) ||
			0;

		if (!loyal || totalDeposit !== PriceHelper.calculateTotalPrice(rooms)) {
			return totalDeposit;
		}
		return totalDeposit - loyal;
	}

	static calculateTotalTaxes(rooms: PriceCalculationInterface[]): number {
		return rooms?.reduce((acc, obj) => acc + obj.pricing.tax.total, 0) || 0;
	}

	static getPricingTooltip(
		occupancyRestrictions: OccupancyRestrictionsInterface,
	): { persons: number; rooms: number } {
		return occupancyRestrictions.adults.max === 1
			? { persons: 1, rooms: 1 }
			: { persons: 2, rooms: 1 };
	}
}
